import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { Languages, Save, RotateCcw, ArrowLeft, ExternalLink } from "lucide-react";

const CATS = [{ v: "moto", l: "Moto & scutere" }, { v: "oem", l: "OEM / ODM" }, { v: "parts", l: "Piese & accesorii" }];
const LANGS = [{ v: "it", l: "Italiano" }, { v: "zh", l: "简体中文" }, { v: "hi", l: "हिन्दी" }, { v: "vi", l: "Tiếng Việt" }, { v: "en", l: "English" }];
const kind = k => (k.endsWith(".q") ? "Întrebare" : k.endsWith(".help") ? "Explicație" : "Răspuns");

function Row({ k, lang, en, value, edited, onSaved }) {
  const [text, setText] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => setText(value), [value]);
  const testid = `tr-${k.replace(/\./g, "-")}`;
  const save = async (reset = false) => {
    setBusy(true);
    try {
      await api.put("/admin/eicma/translations", { key: k, lang, text: reset ? "" : text });
      toast.success(reset ? "Traducerea IA a fost restabilită" : "Salvat");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    setBusy(false);
  };
  return (
    <div className={`py-3 border-b border-border last:border-0 ${k.endsWith(".q") ? "mt-4" : "pl-4"}`} data-testid={testid}>
      <div className="flex items-start gap-2 mb-1">
        <p className="text-xs text-muted-foreground flex-1"><span className="font-semibold">{kind(k)}:</span> {en}</p>
        {edited && <Badge className="bg-[#ff7b21] text-black" data-testid={`${testid}-edited`}>corectat</Badge>}
      </div>
      <Textarea dir="auto" rows={k.endsWith(".q") ? 2 : 1} value={text} onChange={e => setText(e.target.value)} data-testid={`${testid}-input`} />
      <div className="flex gap-2 mt-2">
        <Button size="sm" disabled={busy || text.trim() === value.trim() || !text.trim()} onClick={() => save()} data-testid={`${testid}-save`}><Save className="w-3 h-3 mr-1" />Salvează</Button>
        {edited && <Button size="sm" variant="outline" disabled={busy} onClick={() => save(true)} data-testid={`${testid}-reset`}><RotateCcw className="w-3 h-3 mr-1" />Revino la IA</Button>}
      </div>
    </div>
  );
}

export default function EicmaTranslationsPage() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("moto");
  const [lang, setLang] = useState("it");
  const load = () => api.get("/admin/eicma/translations").then(r => setData(r.data)).catch(() => toast.error("Eroare la încărcare"));
  useEffect(() => { load(); }, []);

  return (
    <Layout>
      <Card data-testid="eicma-translations-page" className="max-w-4xl">
        <CardHeader>
          <Link to="/eicma-leads" className="text-sm text-muted-foreground flex items-center gap-1 mb-2"><ArrowLeft className="w-4 h-4" />EICMA</Link>
          <CardTitle className="flex items-center gap-2"><Languages className="w-5 h-5" />Relecture traduceri chestionare</CardTitle>
          <p className="text-sm text-muted-foreground">Textul englez este originalul. Corectează întrebarea sau răspunsurile propuse și apasă „Salvează”; apare imediat pe formular.</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-3">{CATS.map(c => <Button key={c.v} size="sm" variant={cat === c.v ? "default" : "outline"} onClick={() => setCat(c.v)} data-testid={`tr-cat-${c.v}`}>{c.l}</Button>)}</div>
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            {LANGS.map(l => <Button key={l.v} size="sm" variant={lang === l.v ? "default" : "outline"} onClick={() => setLang(l.v)} data-testid={`tr-lang-${l.v}`}>{l.l}</Button>)}
            <a href={`/eicma/${cat}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary flex items-center gap-1 ml-auto" data-testid="tr-preview"><ExternalLink className="w-4 h-4" />Vezi formularul</a>
          </div>
          {!data ? <p className="text-sm text-muted-foreground">...</p> : data.keys[cat].map(k => {
            const ok = `${k}|${lang}`;
            return <Row key={`${k}-${lang}`} k={k} lang={lang} en={data.ai[k].en} value={data.overrides[ok] ?? data.ai[k][lang] ?? ""} edited={ok in data.overrides} onSaved={load} />;
          })}
        </CardContent>
      </Card>
    </Layout>
  );
}
